import React, { useState, memo, useEffect, useCallback, useId } from 'react';
import { editIconPurple } from '../../../../../../assets/hostedassets';
import { CellRendererProps } from '../../../DataTable.types';
import styles from './NumberCellRenderer.module.scss';
import clsx from 'clsx';
import LoadingSpinner from '../../../../LoadingSpinner/LoadingSpinner';
import { isEmpty } from '../../../../../../utils/utils';

/**
 * NumberCellRenderer is a component that renders a number cell in a table.
 * The units can be added to the cell by adding unit key to the column.
 */
export const NumberCellRenderer: React.FC<CellRendererProps> = memo(({ column, dynamicProps, row, value }) => {
    const { updateCustomFieldStatus, isDisabled } = dynamicProps ?? {};
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState<number | undefined>(value);
    const [loading, setLoading] = useState(false);
    const generatedId = useId();

    const hasValue = !isEmpty(value);

    useEffect(() => {
        if (!isEditing) {
            setEditValue(value);
        }
    }, [value, isEditing]);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDisabled) return;
        setIsEditing(true);
        setEditValue(value);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        if (inputValue === '') {
            setEditValue(undefined);
        } else {
            const numValue = Number(inputValue);
            setEditValue(isNaN(numValue) ? undefined : numValue);
        }
    };

    const handleInputBlur = () => {
        setIsEditing(false);
        if (editValue === value) return;

        const handlers = {
            onStart: () => setLoading(true),
            onSuccess: () => setLoading(false),
            onError: () => setLoading(false),
        };
        updateCustomFieldStatus(row, { value: editValue }, column, handlers);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleInputBlur();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setEditValue(value);
        }
    };

    const getFormattedValue = useCallback((val: string) => {
        const numberValue = String(val) || '-';
        return numberValue.length > 5 ? numberValue.substring(0, 5) + '...' : numberValue;
    }, []);

    if (isEditing) {
        return (
            <div className={styles.numberCellRenderer}>
                <div className={styles.inputContainer}>
                    <input
                        type="number"
                        id={`number-input-${row.id || row.index || generatedId}`}
                        name={`number-input-${column.field || 'value'}`}
                        value={editValue ?? ''}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        onKeyDown={handleInputKeyDown}
                        onClick={(e) => e.stopPropagation()}
                        className={styles.numberInput}
                        autoFocus
                    />
                    {column?.unit && <span className={styles.inputUnit}>{column.unit}</span>}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.numberCellRenderer}>
            <div
                className={clsx(styles.numberCellRendererValue, hasValue && styles.hasValue, isDisabled && styles.disabled)}
                onClick={handleClick}
                style={{ cursor: isDisabled ? 'default' : 'pointer' }}
            >
                {loading ? (
                    <LoadingSpinner color="#6021B3" />
                ) : hasValue ? (
                    <>
                        <span title={value} className={styles.valueText}>
                            {column?.unitLeft && column?.unit && <span>{column.unit} </span>}
                            {getFormattedValue(value)}
                            {!column?.unitLeft && column?.unit && <span> {column.unit}</span>}
                        </span>
                        {!isDisabled && <img src={editIconPurple} alt="Edit" className={styles.editIcon} />}
                    </>
                ) : (
                    <>
                        <div className={styles.emptyValue}>-</div>
                        {!isDisabled && (
                            <div className={styles.addContainer}>
                                <span className={styles.addText}>Add</span>
                                <img src={editIconPurple} alt="Add" className={styles.editIcon} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
});
